import { Typography } from '@/components/atoms';
import { DirhamIcon } from '@/components/atoms/icons';
import { useUIColors } from '@/core/hooks';
import React from 'react';
import { StyleSheet, View } from 'react-native';

const MAJOR_TICK_LINE_HEIGHT = 9;
const MINOR_TICK_LINE_HEIGHT = 6;
const TICK_LINE_GAP = 4;
const TICK_LABEL_WIDTH = 28;

// Reserved vertical space the parent's spacer needs below the track for these tick marks/labels.
export const TICK_MARKS_RESERVED_HEIGHT = MAJOR_TICK_LINE_HEIGHT + TICK_LINE_GAP + 8;

export interface SliderEndLabelProps {
    label: string;
}

export function SliderEndLabel({ label }: Readonly<SliderEndLabelProps>) {
    const colors = useUIColors();
    return (
        <View style={styles.endLabel}>
            <DirhamIcon size={14} color={colors.fontHighContrast} />
            <Typography variant="caption" color="primary" style={styles.endLabelText}>
                {label}
            </Typography>
        </View>
    );
}

export interface SliderTickMarksProps {
    minorTicks: number[];
    majorTicks: number[];
    nearestTick: number | undefined;
    unit: string;
    getPosition: (value: number) => number;
}

/**
 * Memoized — while the slider is being dragged, the parent commits its `value`
 * state (which nearestTick derives from) up to once per animation frame. Without
 * memo, that re-renders these ~16 tick views (each with fresh inline styles) on
 * every frame; with it, they only re-render when nearestTick (or the ticks/unit)
 * actually changes, i.e. when the thumb crosses into a different nearest tick.
 */
export const SliderTickMarks = React.memo(function SliderTickMarks({ minorTicks, majorTicks, nearestTick, unit, getPosition }: Readonly<SliderTickMarksProps>) {
    const colors = useUIColors();

    return (
        <>
            {minorTicks.map((tick) => {
                const isActive = tick === nearestTick;
                const pos = getPosition(tick);
                return (
                    <View key={`minor-${tick}`} style={[styles.minorTickMark, { left: pos }]}>
                        <View
                            style={[
                                styles.minorTickLine,
                                { backgroundColor: isActive ? colors.bloomRedDark : colors.backgroundLevel4 },
                            ]}
                        />
                    </View>
                );
            })}
            {majorTicks.map((tick) => {
                const isActive = tick === nearestTick;
                const tickColor = isActive ? colors.bloomRedDark : colors.backgroundLevel4;
                const pos = getPosition(tick);
                return (
                    <View key={tick} style={[styles.tickMark, { left: pos - TICK_LABEL_WIDTH / 2 }]}>
                        <View style={[styles.tickLine, { backgroundColor: tickColor }]} />
                        <Typography
                            variant="caption"
                            color={isActive ? 'bloomRedDark' : 'tertiary'}
                            style={styles.tickLabel}
                        >
                            {tick} {unit}
                        </Typography>
                    </View>
                );
            })}
        </>
    );
});

const styles = StyleSheet.create({
    endLabel: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 2,
    },
    endLabelText: {
        fontFamily: 'Matter-SemiBold',
        fontWeight: '600',
        fontSize: 10,
        lineHeight: 13,
        letterSpacing: 0,
    },
    minorTickMark: {
        position: 'absolute',
    },
    minorTickLine: {
        width: 1,
        height: MINOR_TICK_LINE_HEIGHT,
    },
    tickMark: {
        position: 'absolute',
        width: TICK_LABEL_WIDTH,
        alignItems: 'center',
    },
    tickLine: {
        width: 1.25,
        height: MAJOR_TICK_LINE_HEIGHT,
        marginBottom: TICK_LINE_GAP,
    },
    tickLabel: {
        textAlign: 'center',
        fontFamily: 'Matter-Regular',
        fontWeight: '400',
        fontSize: 8,
        lineHeight: 8,
        letterSpacing: 0,
    },
});
